import { Component, OnInit } from '@angular/core';
import {Project} from "@catcore/types/api-v2";
import {DisplayTypes} from "@catui/components/panel/types";
import {Subscription} from "rxjs";
import {StyleType} from "@catui/types";
import {ProjectService} from "@catcore/services";
import {BreadcrumbService} from "@catmodules/widgets/breadcrumb/services/breadcrumb.service";
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {isNullOrUndefined} from "util";
import {ProjectGgwDynamicRouteService} from "@catmodels/ggw-dynamic-routes/services/project-ggw-dynamic-route.service";
import {ModifyGgwDynamicRouteComponent} from "@catmodels/ggw-dynamic-routes/components/modify-ggw-dynamic-route/modify-ggw-dynamic-route.component";

@Component({
  selector: 'catapp-ggw-dynamic-routes',
  templateUrl: './ggw-dynamic-routes.component.html',
  styleUrls: ['./ggw-dynamic-routes.component.scss']
})
export class GgwDynamicRoutesComponent implements OnInit {

  tableItems = [];
  dataTableInitialized: boolean;
  project: Project;

  ggwSourceDevices = [];
  ggwSinkDevices = [];
  ggwDefaultRoutes = {};
  ggwDynamicRoutes = [];

  panelDisplayType = DisplayTypes;

  cols = [];
  staticCols = [
    { field: 'parentZoneLabel', header: 'Zone', parentShortName: 'GGW Device', static: true },
    { field: 'deviceName', header: 'Device', parentShortName: 'GGW Device', static: true },
    { field: 'resolvedConfigName', header: 'Config Name', parentShortName: 'GGW Device', static: true },
  ];

  groupCols = [];
  staticGroupCols = [
    { shortName: 'GGW Device', count: this.staticCols.length, children: this.staticCols }
  ];

  projectSubscription: Subscription;
  ggwDeviceSubscription: Subscription;
  ggwDefaultRoutesSubscription: Subscription;
  ggwDynamicRoutesSubscription: Subscription;

  devicesReady: boolean;
  defaultRoutesReady: boolean;
  dynamicRoutesReady: boolean;

  styleType = StyleType;

  constructor(private projectService: ProjectService,
              private breadcrumbService: BreadcrumbService,
              private modalService: ModalService,
              private ggwDynamicRouteService: ProjectGgwDynamicRouteService,
              private resourceService: GenericResourceService) { }

  ngOnInit() {
    this.projectSubscription = this.projectService.getCurrentProject()
      .subscribe((project) => {
        this.project = project;
        const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
        if (isActiveProject) {
          this.dataTableInitialized = false;
          this.devicesReady = false;
          this.defaultRoutesReady = false;
          this.dynamicRoutesReady = false;

          this.ggwDeviceSubscription = this.projectService.getAllEndpointGgwDevices(this.project)
            .subscribe((ggwDevices: any[]) => {
              this.ggwSourceDevices = ggwDevices.filter((device) => device.source === true);
              this.ggwSinkDevices = ggwDevices.filter((device) => device.sink === true);
              this.devicesReady = true;
              this.updateDataTable();
            });

          this.ggwDefaultRoutesSubscription = this.projectService.getGgwDefaultRoutes(this.project)
            .subscribe((defaultRoutes) => {
              this.ggwDefaultRoutes = defaultRoutes;
              this.defaultRoutesReady = true;
              this.updateDataTable();
            });

          this.ggwDynamicRoutesSubscription = this.projectService.getGgwDynamicRoutes(project)
            .subscribe((dynamicRoutes) => {
              this.ggwDynamicRoutes = dynamicRoutes;
              this.dynamicRoutesReady = true;
              this.updateDataTable();
            });
        }
      });

    this.breadcrumbService.update([{label: 'Dynamic Routes', value: 'ggwDynamicRoutes' }]);
  }

  ngOnDestroy() {
    this.projectSubscription.unsubscribe();
    this.ggwDynamicRoutesSubscription.unsubscribe();
    this.ggwDeviceSubscription.unsubscribe();
    this.ggwDefaultRoutesSubscription.unsubscribe();
  }

  /**
   * Returns true if the given column name belongs to a static column.
   *
   * @param colName
   */
  isStaticCol(colName) {
    return this.staticCols.find((c) => c.field === colName);
  }

  /**
   * Returns true if a route exists from the source (row) to the sink (column).
   *
   * @param rowItem
   * @param field
   */
  isActive(rowItem, field) {
    if (this.isStaticCol(field)) {
      return false;
    }
    return !this.isInactive(rowItem, field);
  }

  /**
   * Returns true if the cell has been edited by the user, i.e. the default template
   * has been changed, or the default template assignment (after activating the route manually)
   * has been changed.
   *
   * @param data
   * @param field
   */
  isActiveEdited(data, field) {
    if (this.isStaticCol(field)) {
      return false;
    }
    return !this.isInactive(data, field)
      && data.routes.hasOwnProperty(field)
      && ((
        data.defaultRoutes.hasOwnProperty(field)
          && this.getTemplateId(data.routes[field]) !== data.defaultRoutes[field].id
      ) || (
        !data.defaultRoutes.hasOwnProperty(field) && this.getModelUri(data.routes[field]) === null
      ));
  }

  /**
   * Returns true if the cell has not been edited by the user.
   *
   * @param data
   * @param field
   */
  isActiveNotEdited(data, field) {
    if (this.isStaticCol(field)) {
      return false;
    }
    return !this.isInactive(data, field) && !this.isActiveEdited(data, field);
  }

  /**
   * Returns true if the cell is inactive, i.e. no route exists.
   *
   * @param data
   * @param field
   */
  isInactive(data, field: any) {
    if (this.isStaticCol(field)) {
      return false;
    }

    return (!data.routes.hasOwnProperty(field) && !data.defaultRoutes.hasOwnProperty(field))
      || (data.routes.hasOwnProperty(field) && this.isDeactivateDefault(data.routes[field]));
  }

  /**
   * Returns true if the default policy differs from the current policy.
   *
   * @param rowItem
   * @param colField
   */
  hasDifferentDefault(rowItem, colField) {
    return rowItem.routes.hasOwnProperty(colField)
      && rowItem.defaultRoutes.hasOwnProperty(colField)
      && (this.isDeactivateDefault(rowItem.routes[colField])
        || rowItem.routes[colField].id !== rowItem.defaultRoutes[colField].id
      );
  }

  /**
   * Returns the tooltip title for a cell.
   *
   * @param rowItem
   * @param col
   */
  getTitle(rowItem, col) {
    if (col.static) {
      return col.header;
    }

    let title = '';

    if (rowItem.routes.hasOwnProperty(col.field) && !this.isDeactivateDefault(rowItem.routes[col.field])) {
      title = 'Route: ' + rowItem.routes[col.field]._links.template.label;
    }

    if (rowItem.defaultRoutes.hasOwnProperty(col.field) &&
        (!rowItem.routes.hasOwnProperty(col.field) || !this.isDeactivateDefault(rowItem.routes[col.field]))) {
      title = 'Route: ' + rowItem.defaultRoutes[col.field].name;
    }

    if (this.isInactive(rowItem, col.field)) {
      title += (title === '' ? '' : '. ') + 'Click to add ' + rowItem.ggwDevice.label + ' -> ' + col.header;
    }

    if (this.isActive(rowItem, col.field)) {
      title += (title === '' ? '' : '. ') + 'Click to remove ' + rowItem.ggwDevice.label + ' -> ' + col.header;
    }

    return title;
  }

  /**
   * Handles a cell click. If a data cell has been clicked, a dynamic route is either added or
   * removed.
   *
   * Routes that are active by default are deactivated by adding a new GgwDynamicRoute entity
   * with the template set to null.
   *
   * @param rowItem
   * @param col
   */
  cellClick(rowItem, col) {
    if (col.static) {
      return;
    }

    let addRoute = true;
    if (rowItem.routes.hasOwnProperty(col.field)) {
      if (!this.isDeactivateDefault(rowItem.routes[col.field])) {
        // Explicitly set route should be removed
        addRoute = false;
      }
    } else {
      if (rowItem.defaultRoutes.hasOwnProperty(col.field)) {
        // Default route should be removed
        addRoute = false;
      }
    }

    if (addRoute) {
      // Route should be added
      if (rowItem.routes.hasOwnProperty(col.field)) {
        // If a route entity already exists, remove the existing one first
        this.deleteItem(rowItem.routes[col.field]);
      }
      if (!rowItem.defaultRoutes.hasOwnProperty(col.field)) {
        // If no default exists: Add a new route entity. If a default route exists,
        // we're already done, since it will be visible to the user once the overriding
        // route entity (with template set to null) has been removed, which was done
        // above.
        this.addItem(
          rowItem.ggwDevice._links.self.href,
          col.field,
          false);
      }
    } else {
      // Route should be removed
      if (rowItem.routes.hasOwnProperty(col.field)) {
        if (rowItem.defaultRoutes.hasOwnProperty(col.field)) {
          // Existing route entity can be updated to hide the default route.
          this.updateItem(rowItem.routes[col.field], true);
        } else {
          // Remove route entity
          this.deleteItem(rowItem.routes[col.field]);
        }
      } else {
        // Remove default route by adding a new route entity with template set to null
        this.addItem(
          rowItem.ggwDevice._links.self.href,
          col.field,
          true
        );
      }
    }

  }

  /**
   * Handles a click on the edit button of a cell.
   *
   * @param rowItem
   * @param colField
   */
  editButtonClick(rowItem, colField) {
    if (rowItem.routes.hasOwnProperty(colField)) {
      const ggwDynamicRoute = rowItem.routes[colField];
      this.openModalForDynamicRoute(ggwDynamicRoute);
    } else if (rowItem.defaultRoutes.hasOwnProperty(colField)) {
      const sink = this.getSinkFromUri(colField);
      const ggwDynamicRoute = this.buildDynamicRouteFromDefault(rowItem.ggwDevice, sink, rowItem.defaultRoutes[colField]);
      this.openModalForDynamicRoute(ggwDynamicRoute);
    }
  }

  /**
   * Adds a new dynamic route entity.
   *
   * @param sourceUri
   * @param sinkUri
   * @param removeDefault
   */
  private addItem(sourceUri, sinkUri, removeDefault: boolean) {
    this.projectService.addGgwDynamicRoute(this.project,
      {
        source: sourceUri,
        sink: sinkUri,
        removeDefault: removeDefault
      }).subscribe((dynamicRoute) => {
        this.afterGgwDynamicRouteCreate(dynamicRoute);
    });
  }

  /**
   * Updates an existing dynamic route entity.
   *
   * @param dynamicRoute
   * @param removeDefault
   */
  private updateItem(dynamicRoute, removeDefault: boolean) {
    const service = this.resourceService.findServiceForResource(dynamicRoute._links.self.href);
    const data = {};
    if (removeDefault) {
      data['template'] = null;
    }
    const subscr = service.save(data)
      .finally(() => subscr.unsubscribe())
      .subscribe((updatedRoute) => {
        this.afterGgwDynamicRouteUpdate(updatedRoute);
      });
  }

  /**
   * Deletes a given ggw dynamic route entity.
   *
   * @param item the ggw dynamic route
   */
  private deleteItem(item) {
    this.ggwDynamicRouteService
      .delete(item)
      .subscribe((result) => {
        let index = this.ggwDynamicRoutes.indexOf(item);
        this.tableItems
          .filter((tableItem) => {return item._links.source.href === tableItem.ggwDevice._links.self.href})
          .forEach((tableItem) => {
            if (tableItem.routes.hasOwnProperty(item._links.sink.href)) {
              delete tableItem.routes[item._links.sink.href];
            }
          });
        this.ggwDynamicRoutes.splice(index, 1);
      });
  }

  private updateDataTable() {
    if (!this.devicesReady && !this.defaultRoutesReady) {
      return;
    }

    this.updateCols();

    this.tableItems = [];
    this.ggwSourceDevices.forEach((ggwDevice) => {
      this.addSourceGgwDevice(ggwDevice);
    })
  }

  private updateCols() {
    let dataCols = [];
    this.ggwSinkDevices.forEach((ggwDevice) => {
      const col = {
        field: ggwDevice._links.self.href,
        header: ggwDevice.label,
        parentShortName: ggwDevice._links.gui_zone.label,
        static: false
      };
      dataCols.push(col);
    });
    dataCols.sort((a, b) => {
      let parentZoneCmp = a.parentShortName.toUpperCase().localeCompare(b.parentShortName.toUpperCase());
      if (parentZoneCmp !== 0) {
        return parentZoneCmp;
      }
      return a.header.toUpperCase().localeCompare(b.header.toUpperCase());
    });

    let colspanCount = dataCols.reduce((p, c) => {
      let name = c.parentShortName;
      if (!p.hasOwnProperty(name)) {
        p[name] = {
          count: 0,
          children: []
        };
      }
      p[name].count++;
      p[name].children.push(c);
      return p;
    }, {});

    let dataColspanCount = Object.keys(colspanCount).map(k => {
      return {
        shortName: k,
        count: colspanCount[k].count,
        children: colspanCount[k].children
      };
    });

    this.groupCols = [
      ...this.staticGroupCols,
      ...dataColspanCount
    ];
    this.cols = [
      ...this.staticCols,
      ...dataCols
    ];
  }

  private updateTableItem(dynamicRoute) {
    this.tableItems
      .filter((item) => {return item.ggwDevice._links.self.href === dynamicRoute._links.source.href})
      .forEach((item) => {
        item.routes[dynamicRoute._links.sink.href] = dynamicRoute;
      });
  }

  private afterGgwDynamicRouteCreate(dynamicRoute) {
    this.ggwDynamicRoutes.push(dynamicRoute);
    this.updateTableItem(dynamicRoute);
  }

  private afterGgwDynamicRouteUpdate(updatedRoute) {
    let index = this.ggwDynamicRoutes.findIndex((route) => route.id === updatedRoute.id);
    if (index > -1) {
      this.ggwDynamicRoutes.splice(index, 1);
    }
    this.ggwDynamicRoutes.push(updatedRoute);
    this.updateTableItem(updatedRoute);
  }

  private addSourceGgwDevice(ggwDevice) {
    const item = {
      'parentZoneLabel': ggwDevice._links.gui_zone.label,
      'ggwDevice': ggwDevice,
      'deviceName': ggwDevice.name,
      'configName': ggwDevice.config_name,
      'aircraftDevice': ggwDevice.aircraft_device ? 'Yes' : 'No',
      'resolvedConfigName': ggwDevice.resolved_config_name,
      'routes': this.getRoutes(ggwDevice),
      'defaultRoutes': this.getDefaultRoutes(ggwDevice)
    };

    this.tableItems = [...this.tableItems, item];
  }

  private getDefaultRoutes(ggwDevice) {
    if (this.ggwDefaultRoutes.hasOwnProperty(ggwDevice._links.self.href)) {
      return this.ggwDefaultRoutes[ggwDevice._links.self.href];
    } else {
      return {};
    }
  }

  private getRoutes(ggwDevice) {
    const routes = {};
    this.ggwDynamicRoutes
      .filter((route) => route._links.source.href === ggwDevice._links.self.href)
      .forEach((item) => {
        routes[item._links.sink.href] = item;
      });
    return routes;
  }

  /**
   * Builds a new GgwDynamicRoute for the given source/sink-combination and the given template.
   *
   * @param source
   * @param sink
   * @param defaultRouteTemplate
   */
  private buildDynamicRouteFromDefault(source, sink, defaultRouteTemplate) {
    const ggwDynamicRoute = {
      _links: {
        source: source._links.self,
        sink: sink._links.self,
        template: {
          href: '/api/project-ggw-dynamic-route-templates/' + defaultRouteTemplate.id
        }
      },
      _embedded: {
        source: source,
        sink: sink,
        template: defaultRouteTemplate
      }
    };
    if (defaultRouteTemplate.hasOwnProperty('model')) {
      ggwDynamicRoute._embedded.template['_links'] = {
        model: {
          href: '/api/ggw-dynamic-route-templates/' + defaultRouteTemplate.model.id
        }
      }
    }
    return ggwDynamicRoute;
  }

  private openModalForDynamicRoute(dynamicRoute) {
    const compRef = this.modalService.open(ModifyGgwDynamicRouteComponent);
    compRef.componentInstance.project = this.project;
    compRef.componentInstance.ggwDynamicRoute = dynamicRoute;
    compRef.componentInstance.afterCreate.subscribe((dynamicRoute) => this.afterGgwDynamicRouteCreate(dynamicRoute));
    compRef.componentInstance.afterUpdate.subscribe((dynamicRoute) => this.afterGgwDynamicRouteUpdate(dynamicRoute));
    return compRef;
  }

  private isDeactivateDefault(route: any) {
    return !route._links.hasOwnProperty('template');
  }

  private getSinkFromUri(sinkUri: string) {
    return this.ggwSinkDevices.find((sink) => sink._links.self.href === sinkUri);
  }

  private getModelUri(dynamicRoute) {
    if (dynamicRoute && dynamicRoute._embedded && dynamicRoute._embedded.template
      && dynamicRoute._embedded.template._links  && dynamicRoute._embedded.template._links.model) {
      return dynamicRoute._embedded.template._links.model.href;
    }

    return null;
  }

  private getTemplateId(dynamicRoute) {
    if (dynamicRoute._embedded
      && dynamicRoute._embedded.template) {
      return dynamicRoute.id;
    }

    return null;
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/models/ggw-dynamic-routes/components/ggw-dynamic-routes/ggw-dynamic-routes.component.ts